import { GetServerSideProps } from 'next';
import React from 'react';

import { DefaultPage, RenderBlocks } from '@klappir/container/graphcms';
import { getArticle } from '@klappir/data/graphcms';
import { DataGraphcms } from '@klappir/types';
import { Column, Container, Hidden, Row, SEO, Visible } from '@klappir/ui/core';
import { Share, StyledShare } from '@klappir/ui/social';
import { Date, FormattedDate, H3, Paragraph } from '@klappir/ui/text';
import { fonts, rem } from '@klappir/util/styles';
import styled from '@klappir/vendor/styled';

const ArticleHero = styled.div`
  position: relative;

  margin: 0 auto ${rem(120)};
  display: grid;

  grid-template-columns: 0 5% 44% auto;
  grid-column-gap: 36px;
  align-items: start;

  grid-template-areas: '. share summary image';

  @media only screen and (max-width: 860px) {
    margin: 0 auto 0;
    grid-template-columns: 0 0 100% auto;
    grid-column-gap: 18px;
  }

  ${StyledShare} {
    padding: ${rem(105)} 0 0;
    flex-direction: column;
    justify-content: center;

    ${Paragraph} {
      display: none;
    }
  }
`;

const ImageWrap = styled.div<{ image: DataGraphcms.Asset }>`
  display: block;
  background: transparent;

  background-image: url(${({ image }) => image.url});
  background-size: cover;
  background-position: center;

  grid-area: image;

  width: 100%;
  height: 50vh;
  min-height: 30vw;

  @media only screen and (max-width: 800px) {
    height: 30vh;
    width: 90%;
    margin: auto;
    margin-bottom: ${rem(16)};
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  align-items: center;
  padding: ${rem(105)} ${rem(72)} ${rem(16)} 0;

  grid-area: summary;

  ${Date},
  ${H3} {
    margin: 0 0 ${rem(24)};
  }

  @media only screen and (max-width: 800px) {
    padding: ${rem(16)} ${rem(72)} 0 0;
  }
`;

const Split = styled.span`
  margin: 0 ${rem(16)};
  display: inline-block;
  vertical-align: 0.2em;

  border-radius: 100%;

  background: currentColor;
  width: ${rem(5)};

  height: ${rem(5)};
`;

const Authors = styled.div`
  border-top: 1px solid #f6f6f6;
  margin: ${rem(32)} 0;
  padding: ${rem(32)} 0;

  font-size: ${rem(16)};

  display: grid;
  grid-template-columns: 56px 3fr 2fr;
  grid-template-rows: ${rem(32)} ${rem(24)};
  grid-column-gap: 16px;
  grid-template-areas:
    'image name share'
    'image date share';

  date {
    line-height: ${rem(24)};
    grid-area: date;
  }
`;

const AuthorName = styled.span`
  display: block;
  line-height: ${rem(32)};
  font-size: ${rem(16)};
  font-weight: ${fonts.weight.semibold};
  grid-area: name;
`;

const AuthorImage = styled.div<{ image: DataGraphcms.Asset }>`
  background: transparent;

  background-image: url(${({ image }) => image.url});
  background-size: cover;
  background-position: center;

  width: ${rem(56)};
  height: ${rem(56)};

  border-radius: 100%;

  grid-area: image;
`;

const ArticleHeading = styled(H3).attrs({ as: 'h1' })`
  font-weight: ${fonts.weight.semibold};
`;

const Article = ({ content }: DataGraphcms.ArticlePageProps) => {
  enum Category {
    article = 'Article',
    news = 'News',
    success_story = 'Success story',
    webinar = 'Webinar',
    glossary = 'Glossary',
    partners = 'Partners',
  }
  const canonical = content?.slug
    ? `https://www.klappir.com/articles/${content.slug}`
    : undefined;

  return (
    <DefaultPage localizations={content.localizations}>
      <SEO
        title={content?.seo?.title ?? content?.title}
        description={content?.seo?.description ?? content?.summary}
        image={content?.seo?.image ?? content?.image}
        canonical={canonical}
      />

      <ArticleHero>
        <Hidden alwaysRender tiny mobile sm>
          <Share
            url={canonical}
            title={content?.title}
            description={content?.summary}
            direction="column"
            showLabel={false}
          />
        </Hidden>
        <ContentWrapper>
          <div>
            {content?.published && (
              <FormattedDate block date={content.published}>
                <Split /> {Category[content?.category]}
              </FormattedDate>
            )}
            <ArticleHeading>{content?.title}</ArticleHeading>
            <Paragraph>{content?.summary}</Paragraph>
            {content?.authors && (
              <Date>By {content?.authors.map((a) => a.name).join(', ')}</Date>
            )}
          </div>
        </ContentWrapper>
        <Hidden alwaysRender tiny mobile sm>
          {content?.image?.url && <ImageWrap image={content?.image} />}
        </Hidden>
      </ArticleHero>
      <Visible alwaysRender tiny mobile sm>
        {content?.image?.url && <ImageWrap image={content?.image} />}
      </Visible>
      <RenderBlocks blocks={content?.blocks ?? []} />

      <Container>
        <Row>
          <Column tiny={12} sm={10} md={8}>
            <Authors>
              {content?.authors?.[0]?.image && (
                <AuthorImage image={content.authors[0].image} />
              )}

              <AuthorName>
                By {content?.authors.map((a) => a.name).join(', ')}
              </AuthorName>

              <FormattedDate block date={content.published} />

              <Share
                url={canonical}
                title={content?.title}
                description={content?.summary}
              />
            </Authors>
          </Column>
        </Row>
      </Container>
    </DefaultPage>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const slug = (context?.params?.slug as string[]).join();
  const content = await getArticle(slug, context.locale);

  if (!content) {
    return {
      notFound: true,
    };
  }

  return {
    props: { content },
  };
};

export default Article;
